import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Settings,
  Grid3x3,
  PlayCircle,
  LogOut,
  Zap,
  GripVertical,
  ExternalLink,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("gaelon_token", data.token);
      onLogin();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5 noise-overlay">
      <Card className="w-full max-w-sm border-white/10 bg-card">
        <CardHeader>
          <span className="font-display text-lg font-semibold tracking-[0.2em] metallic-sheen">GAELON</span>
          <CardTitle className="mt-2 font-display text-xl font-light">Project Reveal · Admin</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@gaelon.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {err && <p className="text-sm text-red-400" data-testid="admin-login-error">{err}</p>}
            <Button type="submit" className="w-full" data-testid="admin-login-btn" disabled={loading}>
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

const STATUS_OPTIONS = [
  "Planejamento",
  "Design",
  "Desenvolvimento",
  "Integrações",
  "Testes",
  "Homologação",
  "Preparando lançamento",
];

const HERO_PRESETS = [
  "Estamos construindo algo novo.",
  "Algo grande está tomando forma.",
  "Você está vendo o futuro ser construído.",
  "Uma nova solução está surgindo.",
  "45 dias. 45 partes. Uma nova plataforma.",
];

function Dashboard({ onLogout }) {
  const [cfg, setCfg] = useState(null);
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/config");
    setCfg(data.config);
    setState(data.state);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (key, value) => setCfg((c) => ({ ...c, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", cfg);
      setCfg(data.config);
      setState(data.state);
      toast.success("Configurações salvas.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const releaseNext = async () => {
    if (!window.confirm("Liberar o próximo fragmento manualmente? Esta ação será registrada.")) return;
    try {
      const { data } = await api.post("/admin/release-next");
      setState(data.state);
      toast.success("Próximo fragmento liberado.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveSequence = async (seq) => {
    try {
      await api.post("/admin/reveal-sequence", { revealSequence: seq });
      update("revealSequence", seq);
      toast.success("Ordem dos fragmentos salva.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const onDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const seq = [...cfg.revealSequence];
    const [moved] = seq.splice(dragIdx, 1);
    seq.splice(targetIdx, 0, moved);
    setDragIdx(null);
    saveSequence(seq);
  };

  if (!cfg || !state) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  const stat = (label, value, sub) => (
    <Card className="border-white/10 bg-card">
      <CardContent className="p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</p>
        <p className="mt-2 font-mono text-2xl font-light metallic-text">{value}</p>
        {sub && <p className="mt-1 text-xs text-white/40">{sub}</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background text-foreground noise-overlay">
      <header className="glass sticky top-0 z-30 flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <span className="font-display font-semibold tracking-[0.2em] metallic-sheen">GAELON</span>
          <span className="text-xs text-white/40">Project Reveal · Admin</span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white"
            data-testid="view-site-link"
          >
            Ver site <ExternalLink className="h-3 w-3" />
          </a>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="admin-logout-btn">
            <LogOut className="mr-1 h-4 w-4" /> Sair
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="mb-6 bg-white/5">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <LayoutDashboard className="mr-1 h-4 w-4" /> Visão geral
            </TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config">
              <Settings className="mr-1 h-4 w-4" /> Configurações
            </TabsTrigger>
            <TabsTrigger value="fragments" data-testid="tab-fragments">
              <Grid3x3 className="mr-1 h-4 w-4" /> Fragmentos
            </TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview">
              <PlayCircle className="mr-1 h-4 w-4" /> Simular dia
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {stat("Projeto", cfg.projectName)}
              {stat("Dia atual", `${state.currentDay}/${state.totalDays}`)}
              {stat("Progresso", `${state.progressPercentage}%`)}
              {stat("Status", cfg.status)}
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {stat("Etapas concluídas", state.completedDays, `${state.remainingDays} restantes`)}
              {stat("Lançado", state.launched ? "Sim" : "Não", cfg.launchDate)}
            </div>
            <Card className="mt-4 border-white/10 bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-light">
                  <Zap className="h-4 w-4 text-amber-400" /> Liberação emergencial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm text-white/50">
                  Antecipa manualmente a liberação do próximo fragmento (+1 dia). A ação é registrada.
                </p>
                <Button onClick={releaseNext} data-testid="release-next-btn" variant="secondary">
                  Liberar próximo fragmento
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONFIG */}
          <TabsContent value="config">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Nome do projeto">
                <Input data-testid="cfg-projectName" value={cfg.projectName} onChange={(e) => update("projectName", e.target.value)} />
              </Field>
              <Field label="URL final do projeto">
                <Input data-testid="cfg-finalUrl" value={cfg.finalUrl} onChange={(e) => update("finalUrl", e.target.value)} />
              </Field>
              <LogoUpload label="Logo do projeto (Repositor)" which="project" url={cfg.projectLogoUrl} onUploaded={(c) => { setCfg(c); setState((s) => s); }} />
              <LogoUpload label="Logo da Gaelon" which="gaelon" url={cfg.gaelonLogoUrl} onUploaded={(c) => setCfg(c)} />
              <Field label="Data inicial">
                <Input type="date" data-testid="cfg-startDate" value={cfg.developmentStartDate} onChange={(e) => update("developmentStartDate", e.target.value)} />
              </Field>
              <Field label="Data final">
                <Input type="date" data-testid="cfg-endDate" value={cfg.developmentEndDate} onChange={(e) => update("developmentEndDate", e.target.value)} />
              </Field>
              <Field label="Data de lançamento">
                <Input type="date" data-testid="cfg-launchDate" value={cfg.launchDate} onChange={(e) => update("launchDate", e.target.value)} />
              </Field>
              <Field label="Total de dias / fragmentos">
                <Input type="number" data-testid="cfg-totalDays" value={cfg.totalDays}
                  onChange={(e) => {
                    const n = parseInt(e.target.value || "0", 10);
                    update("totalDays", n);
                    update("fragmentCount", n);
                    update("revealSequence", Array.from({ length: n }, (_, i) => i + 1));
                  }} />
              </Field>
              <Field label="Título principal">
                <select
                  data-testid="cfg-heroTitle"
                  className="h-10 w-full rounded-md border border-white/15 bg-transparent px-3 text-sm"
                  value={cfg.heroTitle}
                  onChange={(e) => update("heroTitle", e.target.value)}
                >
                  {HERO_PRESETS.map((p) => (
                    <option key={p} value={p} className="bg-black">{p}</option>
                  ))}
                </select>
              </Field>
              <Field label="Status do desenvolvimento">
                <select
                  data-testid="cfg-status"
                  className="h-10 w-full rounded-md border border-white/15 bg-transparent px-3 text-sm"
                  value={cfg.status}
                  onChange={(e) => update("status", e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-black">{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Subtítulo" full>
                <Textarea data-testid="cfg-subtitle" value={cfg.heroSubtitle} onChange={(e) => update("heroSubtitle", e.target.value)} />
              </Field>
              <Field label="Mensagem atual (sobrescreve fase)" full>
                <Input data-testid="cfg-msgOverride" value={cfg.currentMessageOverride || ""} onChange={(e) => update("currentMessageOverride", e.target.value || null)} placeholder="Deixe vazio para automática" />
              </Field>
              <Field label="Última atualização (texto)">
                <Input data-testid="cfg-lastUpdate" value={cfg.lastUpdateText} onChange={(e) => update("lastUpdateText", e.target.value)} />
              </Field>
              <Field label="Porcentagem manual (opcional)">
                <Input type="number" data-testid="cfg-manualPct" value={cfg.manualProgressPercent ?? ""} onChange={(e) => update("manualProgressPercent", e.target.value === "" ? null : parseInt(e.target.value, 10))} placeholder="Automático" />
              </Field>
            </div>

            <Card className="mt-6 border-white/10 bg-card">
              <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
                <div className="flex items-center gap-3">
                  <Switch data-testid="cfg-autoProgress" checked={cfg.autoProgress} onCheckedChange={(v) => update("autoProgress", v)} />
                  <div>
                    <p className="text-sm text-white">Progresso automático</p>
                    <p className="text-xs text-white/40">Usa as datas. Desligue para definir o dia manualmente.</p>
                  </div>
                </div>
                {!cfg.autoProgress && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-white/50">Dia atual:</Label>
                    <Input type="number" data-testid="cfg-manualDay" className="w-24" value={cfg.manualDay} onChange={(e) => update("manualDay", parseInt(e.target.value || "0", 10))} />
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="mt-6">
              <Button onClick={save} disabled={saving} data-testid="cfg-save-btn">
                {saving ? "Salvando..." : "Salvar configurações"}
              </Button>
            </div>
          </TabsContent>

          {/* FRAGMENTS */}
          <TabsContent value="fragments">
            <p className="mb-4 text-sm text-white/50">
              Arraste para reordenar. A posição na lista define o dia de liberação (posição 1 = Dia 1).
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
              {cfg.revealSequence.map((fragId, idx) => (
                <div
                  key={`${fragId}-${idx}`}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(idx)}
                  data-testid={`fragment-item-${idx}`}
                  className={`flex cursor-grab items-center justify-between rounded-lg border p-3 transition-colors ${
                    idx < state.currentDay ? "border-white/25 bg-white/10" : "border-white/10 bg-white/[0.03]"
                  }`}
                >
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/40">Dia {idx + 1}</p>
                    <p className="font-mono text-sm text-white">fragment-{String(fragId).padStart(2, "0")}</p>
                  </div>
                  <GripVertical className="h-4 w-4 text-white/30" />
                </div>
              ))}
            </div>
          </TabsContent>

          {/* PREVIEW */}
          <TabsContent value="preview">
            <p className="mb-4 text-sm text-white/50">
              Pré-visualize como a página pública aparecerá em um dia específico.
            </p>
            <div className="flex flex-wrap gap-3">
              {[1, 5, 10, 20, 30, 40, cfg.totalDays].map((d) => (
                <a
                  key={d}
                  href={`/?sim=${d}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`simulate-day-${d}`}
                  className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/80 transition-colors hover:border-white/30 hover:text-white"
                >
                  Simular Dia {d}
                </a>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function LogoUpload({ label, which, url, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const inputId = `logo-upload-${which}`;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("which", which);
      form.append("file", file);
      const { data } = await api.post("/admin/upload-logo", form);
      onUploaded(data.config);
      toast.success("Logo atualizado.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no upload.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs text-white/50">{label}</Label>
      <div className="flex items-center gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10" style={{ backgroundImage: "linear-gradient(45deg,#1a1a1a 25%,transparent 25%),linear-gradient(-45deg,#1a1a1a 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#1a1a1a 75%),linear-gradient(-45deg,transparent 75%,#1a1a1a 75%)", backgroundSize: "12px 12px", backgroundPosition: "0 0,0 6px,6px -6px,-6px 0", backgroundColor: "#2a2a2a" }}>
          {url ? (
            <img src={url} alt={label} className="h-full w-full object-contain" data-testid={`logo-preview-${which}`} />
          ) : (
            <span className="text-[10px] text-white/30">sem logo</span>
          )}
        </div>
        <div className="flex-1">
          <input id={inputId} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`logo-input-${which}`} />
          <label
            htmlFor={inputId}
            className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/80 transition-colors hover:border-white/30 hover:text-white"
            data-testid={`logo-upload-btn-${which}`}
          >
            {uploading ? "Enviando..." : "Enviar arquivo"}
          </label>
          <p className="mt-2 text-[10px] text-white/35">PNG, JPEG, WEBP ou SVG · máx 8MB</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-2 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs text-white/50">{label}</Label>
      {children}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  const check = useCallback(async () => {
    const token = localStorage.getItem("gaelon_token");
    if (!token) return setAuthed(false);
    try {
      await api.get("/auth/me");
      setAuthed(true);
    } catch {
      localStorage.removeItem("gaelon_token");
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const logout = () => {
    localStorage.removeItem("gaelon_token");
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }
  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
}
